import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import {
  ActiveCarryOverLoadsResponse,
  CloseActiveCarryOverLoadsRequest,
  CloseActiveCarryOverLoadsResponse,
} from 'src/app/core/models/position-operations/carry-over-loads.model';
import {
  GleLoadsRequest,
  GleLoadsResponse,
  UpdateGleLoadRequest,
  UpdateGleLoadResponse,
} from 'src/app/core/models/position-operations/outbound-scan-gle.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class ActiveCarryOverLoadsService {
  private readonly _http = inject(HttpClient);
  private readonly _commonUtils = inject(CommonUtils);
  private readonly _errorHandler = inject(ErrorhandleService);

  readonly baseUrl = `${environment.apiUrl}/carryoverload`;

  /**
   * Fetches active carry-over loads.
   * @returns Observable of ActiveCarryOverLoadsResponse
   */
  getActiveLoads(): Observable<ActiveCarryOverLoadsResponse> {
    // this._commonUtils.updateLoadingModal(true);
    const loaderTimeout = setTimeout(
      () => this._commonUtils.updateLoadingModal(true),
      0,
    );

    return this._http
      .get<ActiveCarryOverLoadsResponse>(`${this.baseUrl}/get-active-loads`)
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<ActiveCarryOverLoadsResponse>(
            error,
            error.error.code,
            'Active Carry Over Load Service: Get Loads',
            BANNER_TYPE.TABLE_DATA_ERROR,
          ),
        ),
        finalize(() => {
          clearTimeout(loaderTimeout);
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Closes an active carry-over load.
   * @param request - The request payload to close the load.
   * @returns Observable of ActiveCarryOverLoadsResponse
   */
  closeActiveLoad(
    request: CloseActiveCarryOverLoadsRequest,
  ): Observable<CloseActiveCarryOverLoadsResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<CloseActiveCarryOverLoadsResponse>(`${this.baseUrl}/close`, request)
      .pipe(
        take(1),
        catchError((error) => {
          return this._errorHandler.handleError<CloseActiveCarryOverLoadsResponse>(
            error,
            error.error.code,
            'Active Carry Over Load Service: Close Load',
            BANNER_TYPE.API_400_ERROR,
            {} as CloseActiveCarryOverLoadsResponse,
          );
        }),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Fetches GLE loads based on the provided request parameters.
   * @param request - The request payload containing GLE load parameters.
   * @returns Observable of GleLoadsResponse
   */
  getGleLoads(request: GleLoadsRequest): Observable<GleLoadsResponse> {
    this._commonUtils.updateLoadingModal(true);
    const params = new HttpParams({ fromObject: { ...request } });

    return this._http
      .get<GleLoadsResponse>(`${this.baseUrl}/get-gle-loads`, { params })
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<GleLoadsResponse>(
            error,
            error.error.code,
            'Active Carry Over Load Service: Get GLE Load',
            BANNER_TYPE.API_400_ERROR,
            {} as GleLoadsResponse,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Updates a GLE load.
   * @param request - The request payload to update the GLE load.
   * @returns Observable of UpdateGleLoadResponse
   */
  updateGleLoad(
    request: UpdateGleLoadRequest,
  ): Observable<UpdateGleLoadResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<UpdateGleLoadResponse>(`${this.baseUrl}/update`, request)
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<UpdateGleLoadResponse>(
            error,
            error.error.code,
            'Active Carry Over Load Service: Update Load',
            BANNER_TYPE.API_400_ERROR,
            {} as UpdateGleLoadResponse,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }
}
